/**
 * Header Component
 * Manages header functionality including mega menu, mobile menu, cart counter, and dynamic height
 */

class Header {
    constructor() {
        this.header = document.getElementById('header');
        this.swipers = new Map();
        this.resizeObserver = null;

        this.init();
    }

    init() {
        if (!this.header) {
            console.warn('Header element not found');
            return;
        }

        this.initHeaderHeight();
        this.initCartCounter();
        this.initMegaMenu();
        this.initMobileMenu();
        this.initAccountDropdown();

        console.log('Header initialized');
    }

    /**
     * Header Height Management
     */
    initHeaderHeight() {
        const updateHeight = () => {
            const height = this.header.offsetHeight - 1; // Subtract 1px for border
            document.documentElement.style.setProperty('--header-height', `${height}px`);
        };

        // Initial update
        updateHeight();

        // Use ResizeObserver for better performance
        if ('ResizeObserver' in window) {
            this.resizeObserver = new ResizeObserver(updateHeight);
            this.resizeObserver.observe(this.header);
        } else {
            // Fallback for older browsers
            window.addEventListener('resize', this.debounce(updateHeight, 150));
        }
    }

    /**
     * Cart Counter
     */
    initCartCounter() {
        const cartButton = document.getElementById('cart-button');
        if (!cartButton) return;

        const maxItems = parseInt(cartButton.dataset.maxItems, 10) || 0;
        const cartCountSpan = cartButton.querySelector('.header__cart-count');

        if (!cartCountSpan) return;

        const updateCartCount = (itemCount) => {
            const displayMax = itemCount >= maxItems ? itemCount : maxItems;

            if(itemCount > 2) {
              cartCountSpan.textContent = `${itemCount}`;
            } else {
              cartCountSpan.textContent = `${itemCount}/${displayMax}`;
            }
            cartButton.classList.toggle('header__action-button--cart--full', itemCount >= maxItems);
        };

        // Count paid items only (price > 0); fallback to total count if items array unavailable
        const getPaidItemCount = (cartData) => {
            if (!cartData?.items?.length) return cartData?.item_count ?? 0;
            return cartData.items.reduce((sum, item) => sum + ((item.final_price > 0 ? (item.quantity || 1) : 0)), 0);
        };

        // Native theme PubSub: fired by product-form, cart.js, quick-add, bundle-constructor
        if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
            subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
                const cartData = event?.cartData;
                if (!cartData) return;
                updateCartCount(getPaidItemCount(cartData));
            });
        }

        // Bundle drawer custom event
        document.addEventListener('bundle:cart-updated', (event) => {
            const cartData = event?.detail?.cartData;
            if (!cartData) return;
            updateCartCount(getPaidItemCount(cartData));
        });

    }

    /**
     * Mega Menu
     */
    initMegaMenu() {
        const menuLinks = document.querySelectorAll('.header__nav-link');
        const megaMenus = document.querySelectorAll('.header__mega-menu');
        const closeButtons = document.querySelectorAll('.header__action-button--back .header__action-button');

        if (!menuLinks.length || !megaMenus.length) return;

        let closeTimeout;

        // Hide all menus initially
        megaMenus.forEach(menu => menu.style.display = 'none');

        // Event Listeners
        menuLinks.forEach(link => {
            link.addEventListener('click', (e) => {
                if (window.innerWidth > 1024) return;
                const label = link.textContent.trim().toLowerCase();
                const mega = Array.from(megaMenus).find(m => m.dataset.label?.trim().toLowerCase() === label);
                if (mega?.dataset.disableHover === 'true') return;
                e.preventDefault();
                this.handleMegaMenuToggle(link, menuLinks, megaMenus);
            });

            link.addEventListener('mouseenter', () => {
                if (window.innerWidth < 1024) return;
                const label = link.textContent.trim().toLowerCase();
                const mega = Array.from(megaMenus).find(m => m.dataset.label?.trim().toLowerCase() === label);
                if (mega?.dataset.disableHover === 'true') return;
                clearTimeout(closeTimeout);
                this.handleMegaMenuToggle(link, menuLinks, megaMenus);
            });

            link.addEventListener('mouseleave', () => {
                if (window.innerWidth < 1024) return;
                closeTimeout = setTimeout(() => {
                    this.hideAllMegaMenus(menuLinks, megaMenus);
                }, 200);
            });
        });

        megaMenus.forEach(menu => {
            menu.addEventListener('mouseenter', () => {
                clearTimeout(closeTimeout);
            });

            menu.addEventListener('mouseleave', () => {
                if (window.innerWidth < 1024) return;
                closeTimeout = setTimeout(() => {
                    this.hideAllMegaMenus(menuLinks, megaMenus);
                }, 200);
            });
        });


        closeButtons.forEach(button => {
            button.addEventListener('click', () => this.hideAllMegaMenus(menuLinks, megaMenus));
        });

        // Click outside to close
        document.addEventListener('click', (e) => {
            if (window.innerWidth < 1024) return;

            const clickedInsideMenu = Array.from(megaMenus).some(menu => menu.contains(e.target));
            const clickedOnLink = Array.from(menuLinks).some(link => link.contains(e.target));

            if (!clickedInsideMenu && !clickedOnLink) {
                this.hideAllMegaMenus(menuLinks, megaMenus);
            }
        });

        // Close on resize
        window.addEventListener('resize', this.debounce(() => {
            this.hideAllMegaMenus(menuLinks, megaMenus);
        }, 150));
    }

    handleMegaMenuToggle(activeLink, allLinks, allMenus) {
        const label = activeLink.textContent.trim().toLowerCase();

        this.hideAllMegaMenus(allLinks, allMenus);
        activeLink.classList.add('header__nav-link--active');
        this.header.classList.add('header--mega-menu-active');

        allMenus.forEach(menu => {
            const menuLabel = menu.dataset.label?.trim().toLowerCase();

            if (menuLabel === label) {
                menu.style.display = 'flex';
                this.initSwiper(menu);
            }
        });
    }

    hideAllMegaMenus(menuLinks, megaMenus) {
        megaMenus.forEach(menu => menu.style.display = 'none');
        menuLinks.forEach(link => link.classList.remove('header__nav-link--active'));
        this.header.classList.remove('header--mega-menu-active');
    }

    /**
     * Account dropdown: close button hides panel until next mouse leave
     */
    initAccountDropdown() {
        const wrapper = document.getElementById('account-dropdown-wrapper');
        if (!wrapper) return;

        const trigger = wrapper.querySelector('#account-button');
        const closeBtn = wrapper.querySelector('.header__account-dropdown-close');
        const DESKTOP = 1024;

        const isDesktop = () => window.innerWidth >= DESKTOP;

        const openPanel = () => wrapper.classList.add('header__account-dropdown--open');
        const closePanel = () => wrapper.classList.remove('header__account-dropdown--open');
        const togglePanel = () => wrapper.classList.toggle('header__account-dropdown--open');

        // On desktop: block navigation; on mobile/tablet: toggle panel on click
        if (trigger) {
            trigger.addEventListener('click', (e) => {
                e.preventDefault();
                if (!isDesktop()) {
                    togglePanel();
                }
            });
        }

        // Close button
        if (closeBtn) {
            closeBtn.addEventListener('click', (e) => {
                e.preventDefault();
                if (isDesktop()) {
                    wrapper.classList.add('header__account-dropdown--closed');
                } else {
                    closePanel();
                }
            });
        }

        // On desktop: reset --closed state when mouse leaves
        wrapper.addEventListener('mouseleave', () => {
            if (isDesktop()) {
                wrapper.classList.remove('header__account-dropdown--closed');
            }
        });

        // Close on outside click (mobile/tablet)
        document.addEventListener('click', (e) => {
            if (!isDesktop() && !wrapper.contains(e.target)) {
                closePanel();
            }
        });
    }

    /**
     * Swiper Initialization
     */
    initSwiper(menu) {
        const swiperContainer = menu.querySelector('.swiper');
        if (!swiperContainer || typeof Swiper === 'undefined') return;

        const BREAKPOINT = 1024;
        const windowWidth = window.innerWidth;
        let instance = this.swipers.get(swiperContainer);

        if (windowWidth >= BREAKPOINT) {
            if (!instance) {
                instance = new Swiper(swiperContainer, {
                    loop: true,
                    grabCursor: true,
                    height: 320,
                    navigation: {
                        nextEl: menu.querySelector('.header__swiper-button-next'),
                        prevEl: menu.querySelector('.header__swiper-button-prev'),
                    },
                    enabled: false,
                    breakpoints: {
                        [BREAKPOINT]: {
                            enabled: true,
                            slidesPerView: 4,
                            spaceBetween: 32,
                        },
                    },
                });
                this.swipers.set(swiperContainer, instance);
            } else {
                instance.updateAutoHeight?.();
                instance.enable?.();
            }
        } else if (instance) {
            instance.destroy(true, true);
            this.swipers.delete(swiperContainer);
        }
    }

    /**
     * Mobile Menu
     */
    initMobileMenu() {
        const menuButton = document.querySelector('.header__action-button--menu');
        const mobileMenu = document.querySelector('.header-mobile-menu');

        if (!menuButton || !mobileMenu) return;

        menuButton.addEventListener('click', () => this.toggleMobileMenu(mobileMenu));

        window.addEventListener('resize', this.debounce(() => {
            if (window.innerWidth >= 768) {
                this.closeMobileMenu(mobileMenu);
            }
        }, 150));
    }

    toggleMobileMenu(mobileMenu) {
        const isOpen = this.header.classList.contains('header--mobile-menu-active');

        if (!isOpen) {
            this.openMobileMenu(mobileMenu);
        } else {
            this.closeMobileMenu(mobileMenu);
        }
    }

    openMobileMenu(mobileMenu) {
        this.header.classList.add('header--mobile-menu-active');
        mobileMenu.classList.add('header-mobile-menu--active');

        // Scroll to header if needed
        const headerRect = this.header.getBoundingClientRect();
        if (window.scrollY < headerRect.top) {
            window.scrollTo({
                top: headerRect.top + 100
            });
        }

        document.body.classList.add('no-scroll');
    }

    closeMobileMenu(mobileMenu) {
        this.header.classList.remove('header--mobile-menu-active');
        mobileMenu.classList.remove('header-mobile-menu--active');
        document.body.classList.remove('no-scroll');
    }

    /**
     * Utility: Debounce
     */
    debounce(func, wait) {
        let timeout;
        return function executedFunction(...args) {
            const later = () => {
                clearTimeout(timeout);
                func(...args);
            };
            clearTimeout(timeout);
            timeout = setTimeout(later, wait);
        };
    }

    /**
     * Cleanup
     */
    destroy() {
        if (this.resizeObserver) {
            this.resizeObserver.disconnect();
        }

        this.swipers.forEach(swiper => {
            swiper.destroy(true, true);
        });
        this.swipers.clear();
    }
}

// Initialize
document.addEventListener('DOMContentLoaded', () => {
    window.headerInstance = new Header();
});
